import React from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {ReactComponent as FilterIcon} from 'assets/svg/filter2.svg';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {DateRange} from 'components/ui-kit/DateRange';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {NumberRange} from 'components/ui-kit/NumberRange';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Select} from 'components/ui-kit/Select';
import {ORDER_STATUS_COLORS} from 'core/constants/statuses';
import {OrderStatus, OrderTypes, ProductsPackageType} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {listState$, showConfigBlockChanged} from 'models/orders/grid/list';
import {breakpoint, colors} from 'theme';
import {
    GridFieldConfig,
    OrdersAccessors,
    OrdersGridFormattedRow,
    OrdersGridRawRow,
    UserPermission,
} from 'types';
import {useMatchMedia, usePermission} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {mapArrayToI18nOptions, mapEnumToI18nOptions} from 'utils/select';

import {PackageTypesOptions} from '../../../utils/packageTypes-mappers';
import {CheckboxArray} from '../../ui-kit/CheckboxArray';

import {Controls} from './Controls';
import {OrdersGridList} from './List';
import {HeaderControls, Quantity} from './styled';
import {OrdersGridTable} from './Table';

function getStatusColors(status?: OrderStatus) {
    return {color: ORDER_STATUS_COLORS[status ?? 'DEFAULT']};
}

export function convertData(
    data: OrdersGridRawRow[] | undefined,
    t: Function,
): OrdersGridFormattedRow[] {
    if (!data) {
        return [];
    }

    return data.map((elem) => ({
        [OrdersAccessors.id]: handleNil(elem.orderId, (value: string) => (
            <PermissionAccess
                permissions={[
                    UserPermission.ORDER_CREATE_CUSTOMER,
                    UserPermission.ORDER_CREATE_TRANSPORT,
                    UserPermission.ORDER_CREATE_CUSTOMS,
                ]}
                placeholder={value}
            >
                <Link to={generatePath(ROUTE.ORDERS_DETAIL, {id: value})}>{value}</Link>
            </PermissionAccess>
        )),
        [OrdersAccessors.quantity]: handleNil(elem.totalMarkingCodes, (value) => (
            <Quantity>{value}</Quantity>
        )),
        [OrdersAccessors.createdDate]: handleNil(elem.createdAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy HH:mm'),
        ),
        [OrdersAccessors.closedDate]: handleNil(elem.closedAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy HH:mm'),
        ),
        [OrdersAccessors.packagingType]: handleNil(
            elem.packagingType,
            (value: ProductsPackageType) => t(`dictionaries.products.packageType.${value}`),
        ),
        [OrdersAccessors.status]: handleNil(elem.status, (value: OrderStatus) => (
            <StatusWrapper {...getStatusColors(elem.status)}>
                {t('dictionaries.orders.' + value)}
            </StatusWrapper>
        )),
        [OrdersAccessors.participant]: handleNil(elem.participant),
        [OrdersAccessors.orderType]: handleNil(elem.orderType, (value: OrderTypes) =>
            t(`dictionaries.typeByDomestic.${value}`),
        ),
    }));
}

export const OrdersGridPage = () => {
    const {t} = useTranslation();
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);
    const hasParticipantViewPermission = usePermission([
        UserPermission.PERMISSION_MPL_OPERATOR_ADDITIONAL_TABLES_DATA,
    ]);
    const {showConfigBlock} = useStore(listState$);

    const getGridColumns = (t: Function): GridFieldConfig[] => [
        ...(hasParticipantViewPermission
            ? [
                  {
                      id: OrdersAccessors.participant,
                      header: t('pages.orders.participant'),
                      // filter: <Input placeholder={t('Enter value')} />,
                      // isSortable: true,
                      width: 250,
                  },
              ]
            : []),
        {
            id: OrdersAccessors.id,
            header: t('pages.orders.id'),
            filter: <Input placeholder={t('Enter value')} />,
            isSortable: true,
            width: 400,
        },
        {
            id: OrdersAccessors.createdDate,
            header: t('pages.orders.createdDate'),
            filter: <DateRange />,
            isSortable: true,
        },
        {
            id: OrdersAccessors.closedDate,
            header: t('pages.orders.closedDate'),
            filter: <DateRange />,
            isSortable: true,
        },
        {
            id: OrdersAccessors.quantity,
            header: t('pages.orders.quantity'),
            filter: <NumberRange fromFieldProps={{min: 0}} toFieldProps={{min: 0}} />,
            isSortable: true,
        },
        {
            id: OrdersAccessors.packagingType,
            header: t('pages.orders.packagingType'),
            filter: (
                <CheckboxArray
                    name="package-type-filter"
                    options={() =>
                        mapArrayToI18nOptions(
                            PackageTypesOptions,
                            'dictionaries.products.packageType',
                        )
                    }
                />
            ),
            width: 170,
        },
        {
            id: OrdersAccessors.orderType,
            header: t('pages.orders.domestic'),
            // filter: (
            //     <Select options={mapEnumToI18nOptions(OrderTypes, 'dictionaries.typeByDomestic')} />
            // ),
            isSortable: false,
        },
        {
            id: OrdersAccessors.status,
            header: t('pages.orders.status'),
            filter: (
                <Select
                    options={mapEnumToI18nOptions(OrderStatus, 'dictionaries.orders')}
                    isClearable
                    isMulti
                />
            ),
            width: 150,
            isSortable: true,
        },
    ];

    const headerControls = (
        <HeaderControls>
            <Controls />
        </HeaderControls>
    );

    const pageHeaderControls = isMobile ? (
        <Icon
            source={FilterIcon}
            width={18}
            height={18}
            onClick={() => showConfigBlockChanged(!showConfigBlock)}
            fillColor={showConfigBlock ? colors.brandPrimary : colors.black}
        />
    ) : (
        undefined
    );

    return (
        <Page title={t('menu.Orders')} controls={pageHeaderControls}>
            {!isMobile && (
                <OrdersGridTable
                    configFunc={getGridColumns}
                    dataMapper={convertData}
                    headerControls={headerControls}
                    // exportConfig={exportConfig}
                />
            )}
            {isMobile && (
                <OrdersGridList
                    configFunc={getGridColumns}
                    dataMapper={convertData}
                    headerControls={headerControls}
                    // exportConfig={exportConfig}
                />
            )}
        </Page>
    );
};
